import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationAPI } from '../../services/api';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const TYPE_ICONS = {
  tutorial_published: '\uD83D\uDCD6',
  announcement: '\uD83D\uDCE2',
  flashcard_review_due: '🃏',
};

export default function NotificationBell({ onNavigate }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const dropdownRef = useRef(null);

  const fetchCount = useCallback(() => {
    notificationAPI.unreadCount()
      .then(({ data }) => setCount(data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    notificationAPI.list({ limit: 10 })
      .then(({ data }) => {
        // Handle both { notifications: [...] } and array-direct responses
        const list = Array.isArray(data) ? data : (data.notifications || []);
        setNotifications(list);
      })
      .catch((err) => {
        console.error('[NotificationBell] Failed to fetch notifications:', err);
        setNotifications([]);
      })
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleMarkAllRead = () => {
    notificationAPI.markAllRead()
      .then(() => {
        setCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      })
      .catch(() => {});
  };

  const handleClickNotification = (n) => {
    if (!n.read_at) {
      notificationAPI.markRead(n.notification_id).catch(() => {});
      setNotifications((prev) =>
        prev.map((x) =>
          x.notification_id === n.notification_id
            ? { ...x, read_at: new Date().toISOString() }
            : x
        )
      );
      setCount((c) => Math.max(0, c - 1));
    }
    if (n.type === 'tutorial_published' && n.tutorial_id) {
      setOpen(false);
      if (onNavigate) onNavigate(`/tutorials/${n.domain || 'general'}/play/${n.tutorial_id}`);
    } else if (n.type === 'flashcard_review_due' && n.domain) {
      setOpen(false);
      if (onNavigate) onNavigate(`/flashcards/${n.domain}`);
    } else if (n.type === 'announcement' || n.type?.startsWith('capstone_')) {
      setSelectedNotification(n);
      setShowModal(true);
    }
  };

  return (
    <>
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', zIndex: 400 }}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        style={{
          background: open ? 'rgba(146,20,12,0.35)' : 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1.25rem',
          position: 'relative',
          padding: '4px 8px',
          lineHeight: 1,
          height: 64,
          minWidth: 44,
        }}
        aria-label={`Notifications, ${count} unread`}
        aria-expanded={open}
        aria-haspopup="true"
        title="Notifications"
      >
        &#x1F514;
        {count > 0 && (
          <span
            aria-live="polite"
            style={{
              position: 'absolute',
              top: 0,
              right: 2,
              background: '#ef4444',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              borderRadius: '50%',
              minWidth: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="region"
          aria-label="Notifications dropdown"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 6,
            width: 340,
            maxHeight: 420,
            background: '#1E1E24',
            border: '1px solid #3a3a46',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
            zIndex: 500,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            color: '#FFF8F0',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #3a3a46',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#FFF8F0' }}>Notifications</span>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#FFCF99',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#a09890', fontSize: '0.85rem' }}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#a09890', fontSize: '0.85rem' }}>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.notification_id}
                  onClick={() => handleClickNotification(n)}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #2e2e36',
                    background: n.read_at ? 'transparent' : 'rgba(146,20,12,0.15)',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,207,153,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = n.read_at ? 'transparent' : 'rgba(146,20,12,0.15)'; }}
                >
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: 2 }}>
                    {TYPE_ICONS[n.type] || '\uD83D\uDD14'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: n.read_at ? 500 : 600,
                      fontSize: '0.85rem',
                      color: '#FFF8F0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {n.title}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#a09890', marginTop: 2 }}>
                      {n.type === 'announcement' ? 'Announcement' : n.type === 'flashcard_review_due' ? 'Flashcard Review' : n.type?.startsWith('capstone') ? 'Capstone' : 'New lesson'} &middot; {timeAgo(n.created_at)}
                    </div>
                  </div>
                  {!n.read_at && (
                    <span style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#92140C',
                      flexShrink: 0,
                      marginTop: 6,
                    }} />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px',
            borderTop: '1px solid #3a3a46',
            textAlign: 'center',
          }}>
            <button
              onClick={() => {
                setOpen(false);
                if (onNavigate) onNavigate('/notifications');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#FFCF99',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              View all notifications
            </button>
          </div>
        </div>
    )}
  </div>

    {showModal && selectedNotification && (
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={() => setShowModal(false)}
      >
        <div
          style={{
            background: '#1E1E24', border: '1px solid #3a3a46', borderRadius: 12, padding: 28,
            maxWidth: 520, width: '90%', maxHeight: '75vh', overflow: 'auto',
            color: '#FFF8F0',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{ marginTop: 0, color: '#FFF8F0', fontSize: '1.1rem', marginBottom: 12 }}>
            {selectedNotification.title}
          </h3>
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#d8cfc8', fontSize: '0.9rem', margin: '0 0 16px' }}>
            {selectedNotification.body_md || selectedNotification.body || 'No content available.'}
          </p>
          {selectedNotification.link && (
            <button
              onClick={() => {
                setShowModal(false);
                setOpen(false);
                if (onNavigate) onNavigate(selectedNotification.link);
              }}
              style={{
                background: 'none', border: 'none', color: '#FFCF99',
                cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600,
                padding: 0, display: 'block', marginBottom: 16,
              }}
            >
              View Details →
            </button>
          )}
          <div style={{ textAlign: 'right' }}>
            <button
              onClick={() => setShowModal(false)}
              style={{
                background: '#92140C', color: '#FFF8F0', border: 'none',
                borderRadius: 6, padding: '8px 20px', cursor: 'pointer', fontWeight: 600,
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
